import { CH, I, IconBtn } from './MobileAppHeader';
import { parseSelectableValues } from '../../utils/questionDataUtils';

// ピルボタン
const Pill = ({ active, onClick, children }) => (
  <div onClick={onClick} style={{
    padding: '6px 14px', borderRadius: 16,
    background: active ? '#c96442' : 'var(--app-color-surface)',
    color: active ? '#fff' : CH.ink,
    border: `1px solid ${active ? '#c96442' : 'var(--app-color-border)'}`,
    fontSize: 13, fontFamily: CH.serif, fontWeight: 500,
    display: 'inline-flex', alignItems: 'center', gap: 6,
    cursor: 'pointer',
    maxWidth: '100%',
    whiteSpace: 'normal',
    wordBreak: 'break-all',
    lineHeight: 1.4,
  }}>{children}</div>
);

const FilterRow = ({ label, children }) => (
  <div style={{ marginBottom: 20 }}>
    <div style={{
      fontSize: 11, color: CH.muted, marginBottom: 8, fontWeight: 500,
      fontFamily: CH.mono, textTransform: 'uppercase', letterSpacing: 0.5,
    }}>{label}</div>
    {children}
  </div>
);

const PillGroup = ({ items, selected, onToggle, single }) => (
  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
    {items.map((item) => {
      const val = typeof item === 'string' ? item : item.value;
      const label = typeof item === 'string' ? item : item.label;
      const active = Array.isArray(selected) ? selected.includes(val) : selected === val;
      return (
        <Pill key={val} active={active} onClick={() => onToggle(val, single)}>
          {label}
        </Pill>
      );
    })}
  </div>
);

// 進捗バー
const ProgressBar = ({ correct = 0, incorrect = 0, total = 1 }) => {
  const cp = total > 0 ? (correct / total) * 100 : 0;
  const ip = total > 0 ? (incorrect / total) * 100 : 0;
  return (
    <div>
      <div style={{
        height: 10, borderRadius: 5, background: 'var(--app-color-border)',
        overflow: 'hidden', display: 'flex',
      }}>
        <div style={{ width: `${cp}%`, background: '#099268' }} />
        <div style={{ width: `${ip}%`, background: '#c03727' }} />
      </div>
      <div style={{ display: 'flex', gap: 14, marginTop: 8, fontFamily: CH.serif, fontSize: 12, color: CH.muted }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: '#099268', display: 'inline-block' }} />
          正解 {correct}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: '#c03727', display: 'inline-block' }} />
          不正解 {incorrect}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--app-color-border)', border: '1px solid #87867f', display: 'inline-block' }} />
          未回答 {total - correct - incorrect}
        </span>
      </div>
    </div>
  );
};

function MobileFilterScreen({
  // cert meta
  selectedCertification, certFormalName, certVendor, certUserStatus, examGuideUrl,
  // data
  questions, results, progressData,
  // filter state
  selectedTopic, setSelectedTopic,
  categories, selectedCategory, setSelectedCategory,
  tasks, selectedTask, setSelectedTask,
  selectedResult, setSelectedResult,
  questionOrder, setQuestionOrder,
  selectedQuestions, setSelectedQuestions,
  // resume
  lastQuestionId, resumeFilterState, onResume,
  // actions
  onStart, onBack,
}) {
  const vendorColor = certVendor === 'aws' ? '#e8590c' : certVendor === 'microsoft' ? '#1c7ed6' : certVendor === 'google' ? '#34A853' : '#868e96';
  const vendorLabel = certVendor === 'aws' ? 'AWS' : certVendor === 'microsoft' ? 'Microsoft' : certVendor === 'google' ? 'Google' : certVendor;
  const statusMeta = {
    learning:  { label: 'Learning',  bg: '#e7f5ff', fg: '#1c7ed6' },
    certified: { label: 'Certified', bg: '#ebfbee', fg: '#2f9e44' },
    enrolled:  { label: 'Enrolled',  bg: '#f3f0ff', fg: '#7048e8' },
    retired:   { label: 'Retired',   bg: '#f1f3f5', fg: '#868e96' },
  }[certUserStatus];

  const correct   = progressData?.total_correct   ?? 0;
  const incorrect = progressData?.total_incorrect ?? 0;
  const total     = questions?.length ?? 0;

  // トピック一覧
  const topicValues = [...new Set((questions || []).map(q => q.topic?.toString()).filter(Boolean))].sort();

  const toggle = (setter, current) => (val, single) => {
    if (single) {
      setter(current === val ? '' : val);
    } else {
      setter(current.includes(val) ? current.filter(v => v !== val) : [...current, val]);
    }
  };

  // 絞り込み済み問題数（MobileExamView.buildFilteredList と同じロジック）
  let filtered = questions || [];
  if (selectedTopic?.length > 0) filtered = filtered.filter(q => selectedTopic.includes(q.topic?.toString()));
  if (selectedCategory?.length > 0) filtered = filtered.filter(q => parseSelectableValues(q?.category).some(c => selectedCategory.includes(c)));
  if (selectedTask?.length > 0) filtered = filtered.filter(q => parseSelectableValues(q?.task).some(t => selectedTask.includes(t)));
  if (selectedResult?.length > 0) {
    const codeMap = { '正解': 'correct', '不正解': 'incorrect', '除外': 'excluded' };
    const codes = selectedResult.map(l => codeMap[l]).filter(Boolean);
    const inclUnanswered = selectedResult.includes('未回答');
    filtered = filtered.filter(q => {
      const r = results[q.id];
      if (!r) return inclUnanswered;
      return codes.includes(r);
    });
  }

  return (
    <div style={{ background: 'var(--mantine-color-body)', minHeight: '100vh', fontFamily: CH.serif, paddingBottom: 100 }}>
      {/* ヘッダー */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 20,
        background: 'var(--mantine-color-body)',
        borderBottom: '1px solid var(--app-color-border)',
        padding: '8px 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        minHeight: 48,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <IconBtn onClick={onBack}>{I.back}</IconBtn>
          <span style={{ fontSize: 14, fontWeight: 500, color: CH.ink }}>学習設定</span>
        </div>
      </div>

      {/* 資格メタ */}
      <div style={{ padding: '16px 20px 0' }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          {vendorLabel && (
            <span style={{ display: 'inline-flex', alignItems: 'center', background: `${vendorColor}22`, color: vendorColor, fontFamily: CH.serif, fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 4 }}>{vendorLabel}</span>
          )}
          {statusMeta && (
            <span style={{ display: 'inline-flex', alignItems: 'center', background: statusMeta.bg, color: statusMeta.fg, fontFamily: CH.serif, fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 4 }}>{statusMeta.label}</span>
          )}
        </div>
        <div style={{ fontSize: 22, color: CH.ink, letterSpacing: -0.4, lineHeight: 1.25 }}>
          {certFormalName || selectedCertification?.toUpperCase()}
        </div>
        <div style={{ fontFamily: CH.mono, fontSize: 11, color: CH.dim, marginTop: 4, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>{selectedCertification?.toUpperCase()}</span>
          <span>·</span>
          <span>{total} questions</span>
          {examGuideUrl && (
            <>
              <span>·</span>
              <a href={examGuideUrl} target="_blank" rel="noopener noreferrer"
                style={{ color: '#c96442', display: 'inline-flex', alignItems: 'center', gap: 3, textDecoration: 'none' }}>
                Exam Guide {I.external}
              </a>
            </>
          )}
        </div>
      </div>

      {/* 進捗 */}
      <div style={{ padding: '18px 20px 20px' }}>
        <div style={{
          background: 'var(--app-color-surface)', border: '1px solid var(--app-color-border)',
          borderRadius: 8, padding: 14,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: CH.ink }}>進捗</span>
            <span style={{ fontFamily: CH.mono, fontSize: 11, color: CH.muted }}>
              {correct + incorrect} / {total} · {total > 0 ? Math.round(((correct + incorrect) / total) * 100) : 0}%
            </span>
          </div>
          <ProgressBar correct={correct} incorrect={incorrect} total={total} />
        </div>
      </div>

      <div style={{ padding: '0 20px' }}>
        {/* 再開ボタン */}
        {lastQuestionId && (
          <div
            onClick={onResume}
            style={{
              background: 'var(--app-color-surface)',
              border: '1px solid #c96442',
              borderRadius: 10, padding: 14, marginBottom: 22,
              display: 'flex', gap: 12, alignItems: 'center',
              boxShadow: '0 0 0 3px #fdf5f2', cursor: 'pointer',
            }}
          >
            <div style={{ width: 36, height: 36, borderRadius: 8, background: '#c96442', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {I.play}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: CH.ink }}>前回の続きから</div>
              <div style={{ fontSize: 11, color: CH.muted, marginTop: 2 }}>Q.{lastQuestionId}</div>
            </div>
            <div style={{
              height: 30, padding: '0 14px', borderRadius: 8,
              background: '#c96442', color: '#fff',
              fontSize: 13, fontFamily: CH.serif, fontWeight: 500,
              display: 'inline-flex', alignItems: 'center',
            }}>再開</div>
          </div>
        )}

        {/* Topic */}
        {topicValues.length > 0 && (
          <FilterRow label="Topic">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {['すべて', ...topicValues].map(val => {
                const active = val === 'すべて' ? selectedTopic.length === 0 : selectedTopic.includes(val);
                return (
                  <Pill key={val} active={active} onClick={() => {
                    if (val === 'すべて') { setSelectedTopic([]); return; }
                    setSelectedTopic(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
                  }}>{val}</Pill>
                );
              })}
            </div>
          </FilterRow>
        )}

        {/* カテゴリ */}
        {categories?.length > 0 && (
          <FilterRow label={`カテゴリ${selectedCategory.length > 0 ? ` (${selectedCategory.length} 選択)` : ''}`}>
            <PillGroup
              items={categories}
              selected={selectedCategory}
              onToggle={(val) => setSelectedCategory(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])}
            />
          </FilterRow>
        )}

        {/* タスク */}
        {tasks?.length > 0 && (
          <FilterRow label={`タスク${selectedTask.length > 0 ? ` (${selectedTask.length} 選択)` : ''}`}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {tasks.map((val) => {
                const active = selectedTask.includes(val);
                return (
                  <div
                    key={val}
                    onClick={() => setSelectedTask(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])}
                    style={{
                      padding: '8px 12px', borderRadius: 10,
                      background: active ? '#c96442' : 'var(--app-color-surface)',
                      color: active ? '#fff' : CH.ink,
                      border: `1px solid ${active ? '#c96442' : 'var(--app-color-border)'}`,
                      fontSize: 12, fontFamily: CH.serif, fontWeight: 500,
                      cursor: 'pointer', lineHeight: 1.4,
                    }}
                  >{val}</div>
                );
              })}
            </div>
          </FilterRow>
        )}

        {/* 回答結果 */}
        <FilterRow label="回答結果">
          <PillGroup
            items={['未回答', '正解', '不正解', '除外']}
            selected={selectedResult}
            onToggle={(val) => setSelectedResult(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])}
          />
        </FilterRow>

        {/* 出題順序 */}
        <FilterRow label="出題順序">
          <PillGroup
            items={[{ value: 'first', label: '最初から' }, { value: 'random', label: 'ランダム' }, { value: 'last', label: '最後から' }]}
            selected={questionOrder}
            onToggle={(val) => setQuestionOrder(val)}
          />
        </FilterRow>
      </div>

      {/* スティッキー開始ボタン */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: 'var(--mantine-color-body)',
        borderTop: '1px solid var(--app-color-border)',
        padding: '12px 20px 34px',
      }}>
        <div
          onClick={() => onStart()}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: '100%', height: 48,
            background: '#c96442', color: '#fff',
            fontFamily: CH.serif, fontSize: 16, fontWeight: 500,
            borderRadius: 10, cursor: 'pointer',
          }}
        >
          {filtered.length}問で開始
        </div>
      </div>
    </div>
  );
}

export default MobileFilterScreen;
